
import React, { useState, useCallback, useEffect } from 'react';
import PatientInfoPanel from './components/PatientInfoPanel';
import HealthInputPanel from './components/HealthInputPanel';
import AppointmentPanel from './components/AppointmentPanel';
import { Patient, HealthRecord, VitalHistory, ImportantNote, ResidentVisitation } from './types';
import {
  transformProfile,
  transformHealthRecords,
  transformVitalHistory,
  parseImportantNotes,
  parseResidentVisitations,
  buildVisitationStatusUpdate,
  buildImportantNoteEditBody,
  buildImportantNoteUnflagBody,
} from './utils';
import LoginModal from './components/LoginModal';
import { AppointmentIcon, NoteIcon } from './constants';
import CryptoJS from 'crypto-js';
import { API_BASE_URL, API_KEY, getHealthTrackerRole } from './user_constant';
import {
  getTier,
  loadCached,
  mergePage,
  CacheEntry,
  applyAddRecord,
  applyEditNote,
  applyUnflagNote,
  applyVisitCompleted,
} from './cache/healthCache';
import { SnapshotPayload } from './cache/snapshotCache';
import { RawHealthRecord } from './cache/recordsCache';

type WriteResult = { success: true } | { success: false; error: string };

/**
 * Build the `{ records, snapshot }` page payload that `mergePage` consumes from
 * a raw `HcGetCustomerInfoById` `data` object. Records come from
 * `recent_health_records.records`; the snapshot carries the non-paginated
 * `profile`, `important_notes`, `vitals_last_30_days`, and `resident_visitation`
 * fields.
 */
function buildPagePayload(apiData: any): { records: RawHealthRecord[]; snapshot: SnapshotPayload } {
  return {
    records: (apiData?.recent_health_records?.records ?? []) as RawHealthRecord[],
    snapshot: {
      profile: apiData?.profile,
      important_notes: apiData?.important_notes,
      vitals_last_30_days: apiData?.vitals_last_30_days,
      resident_visitation: apiData?.resident_visitation,
    },
  };
}

/**
 * Map a front-end `HealthRecord['type']` back to the raw `event_type` /
 * `source_table` pair that `transformHealthRecords` reads, so an optimistically
 * added record renders with the correct type badge before the reconciling
 * page-1 refresh replaces it with the authoritative server record.
 */
function rawTypeHints(type: HealthRecord['type']): { event_type: string; source_table: string } {
  switch (type) {
    case 'Vitals': return { event_type: 'vitals', source_table: 'h_vital' };
    case 'ADL': return { event_type: 'adl', source_table: 'h_adl' };
    case 'Notes': return { event_type: 'notes', source_table: 'h_note' };
    case 'Emotion': return { event_type: 'emotion', source_table: 'h_emotion' };
    case 'Glasgow Coma Scale (GCS)': return { event_type: 'gcs', source_table: 'h_gcs' };
    case 'Pain': return { event_type: 'pain', source_table: 'h_pain' };
    case 'Photos': return { event_type: 'photo', source_table: 'h_photo' };
    case 'Physiotherapy': return { event_type: 'physio', source_table: 'h_physio' };
    case 'Appointment': return { event_type: 'appointment', source_table: 'h_appointment' };
    case 'Input / Output': return { event_type: 'input', source_table: 'h_input' };
    default: return { event_type: 'notes', source_table: 'h_note' };
  }
}

/**
 * Build a `RawHealthRecord` from a freshly-created front-end `HealthRecord`.
 *
 * `source_table` + `id` form the Record_Key used for dedup/merge, and
 * `event_timestamp` keeps the record sorted newest-first. API timestamps are
 * stored as UTC+8 but parsed as UTC by `transformHealthRecords` (which then
 * subtracts 8 hours), so we add 8 hours here to round-trip back to the local
 * "now" when displayed optimistically.
 */
function buildRawFromHealthRecord(record: HealthRecord): RawHealthRecord {
  const { event_type, source_table } = rawTypeHints(record.type);
  const event_timestamp = new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString();
  return {
    id: record.id,
    source_table,
    event_timestamp,
    event_type,
    event_details: record.summary,
    creator_name: record.recordedBy,
    isLocalUpdate: true,
  };
}

async function performWrite(body: object, endpointFunc: string): Promise<WriteResult> {
  try {
    const res = await fetch(`${API_BASE_URL}?func=${endpointFunc}`, {
      method: 'PUT',
      headers: { 'x-api-key': API_KEY, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    let parsed: any = null;
    try { parsed = await res.json(); } catch { /* swallow */ }
    if (!res.ok || !parsed?.success) {
      return { success: false, error: parsed?.error || `Save failed (HTTP ${res.status}).` };
    }
    return { success: true };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Network error.' };
  }
}

const App: React.FC = () => {
  const [isInputPanelOpen, setIsInputPanelOpen] = useState(false);
  const [isAppointmentPanelOpen, setIsAppointmentPanelOpen] = useState(false);
  
  // State for dynamic data
  const [patient, setPatient] = useState<Patient | null>(null);
  const [records, setRecords] = useState<HealthRecord[]>([]);
  const [vitalHistory, setVitalHistory] = useState<VitalHistory[]>([]);
  const [rawVitals, setRawVitals] = useState<unknown>(undefined);
  const [importantNotes, setImportantNotes] = useState<ImportantNote[]>([]);
  const [residentVisitations, setResidentVisitations] = useState<ResidentVisitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshProgress, setRefreshProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Pagination state for the "Show More" flow. The backend paginates records
  // (20 per page); the frontend is one continuous page. `currentPage` tracks
  // the last page fetched, `hasMoreRecords` mirrors the API `available_next`
  // flag (keeps the button visible while older records remain on the server),
  // and `isLoadingMore` guards against overlapping next-page fetches.
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreRecords, setHasMoreRecords] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Auth State
  const [userRole, setUserRole] = useState<'staff' | 'family' | null>(getHealthTrackerRole());
  const [employeeNumber, setEmployeeNumber] = useState<string | null>(null);

  // Get customerId from URL query parameter, or default to '484'(Dont not change this block)
  // This is specific for external link
  const [customerId, setCustomerId] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const encKey = "CareConcierge";
    const encryptedParam = params.get('customerId');
    if (!encryptedParam) return '484';
    const decoded = decodeURIComponent(encryptedParam);
    const bytes = CryptoJS.AES.decrypt(decoded, encKey);
    const decryptedId = bytes.toString(CryptoJS.enc.Utf8);
    return decryptedId || '484';
  });
  // This is specific for external link

  // State for modern loading indicator
  const [progress, setProgress] = useState(10);
  const [loadingMessage, setLoadingMessage] = useState("Connecting to Care Guardian network...");

  const loadingMessages = [
    "Connecting to Care Guardian network...",
    "Fetching patient profile...",
    "Loading recent health records...",
    "Analyzing vital signs history...",
    "Finalizing dashboard..."
  ];

  // Effect for managing loading animations
  useEffect(() => {
    if (isLoading) {
        setProgress(10); // Start at a small percentage
        setLoadingMessage(loadingMessages[0]);

        const progressInterval = setInterval(() => {
            setProgress(oldProgress => {
                if (oldProgress >= 95) {
                    return 95; // Stall at 95% until data is actually loaded
                }
                const diff = Math.random() * 15;
                return Math.min(oldProgress + diff, 95);
            });
        }, 800);

        let messageIndex = 0;
        const messageInterval = setInterval(() => {
            messageIndex = (messageIndex + 1) % loadingMessages.length;
            setLoadingMessage(loadingMessages[messageIndex]);
        }, 2000);

        // Cleanup intervals when component unmounts or isLoading becomes false
        return () => {
            clearInterval(progressInterval);
            clearInterval(messageInterval);
        };
    }
  }, [isLoading]);

  // Effect to check for stored user role on initial mount
  useEffect(() => {
    const storedUserRole = localStorage.getItem('careGuardianUserRole') as 'staff' | 'family' | null;
    if (storedUserRole) {
      if (storedUserRole === 'staff') {
        const storedEmployeeNumber = localStorage.getItem('careGuardianEmployeeNumber');
        if (storedEmployeeNumber) {
          setUserRole('staff');
          setEmployeeNumber(storedEmployeeNumber);
        }
      } else {
        setUserRole('family');
      }
    }
  }, []);


  /**
   * Run a cached/merged Cache_Entry through the existing transform pipeline and
   * push the results into component state. This preserves the exact transform
   * chain (`transformProfile`, `transformHealthRecords`, `transformVitalHistory`,
   * `parseImportantNotes`, `parseResidentVisitations`) regardless of whether the
   * entry came from `loadCached` or from a freshly merged page (Req 13.3).
   *
   * The records list is always rendered. Snapshot-derived state (profile,
   * vitals, notes, visitations) is only set when a snapshot is present so a
   * records-only cache never crashes the transforms.
   */
  const applyEntryToState = useCallback((entry: CacheEntry) => {
    setRecords(transformHealthRecords(entry.records as any[]));

    const snapshot = entry.snapshot;
    if (snapshot) {
      if (snapshot.profile) {
        setPatient(transformProfile(snapshot.profile));
      }
      setVitalHistory(transformVitalHistory(snapshot.vitals_last_30_days));
      setRawVitals(snapshot.vitals_last_30_days);
      setImportantNotes(parseImportantNotes(snapshot.important_notes));
      setResidentVisitations(parseResidentVisitations(snapshot.resident_visitation));
    }
  }, []);

  /**
   * Fetch page 1 of `HcGetCustomerInfoById`, merge it into the cache via
   * `mergePage` (which refreshes Last_Fetch_Time + snapshot on page 1), and
   * return the resulting Cache_Entry. Throws on network/payload failure so the
   * caller can decide how to surface it (full error view vs. keep cached data).
   */
  const fetchAndMergePage1 = useCallback(async (): Promise<CacheEntry> => {
    const url = `${API_BASE_URL}?func=HcGetCustomerInfoById&customerId=${customerId}&page=1`;
    const response = await fetch(url, {
      headers: {
        'x-api-key': API_KEY
      }
    });

    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();

    if (!result.success || !result.data) {
      throw new Error('API response indicates failure or is missing data.');
    }

    // Reset pagination to page 1 and capture whether the server has more
    // records so the "Show More" button reflects the freshly fetched page.
    // Tolerate either a nested `pagination` object or a flat available_next.
    const recent = result.data?.recent_health_records;
    setCurrentPage(1);
    setHasMoreRecords(
      Boolean(recent?.pagination?.available_next ?? recent?.available_next)
    );

    const now = Date.now();
    return mergePage(customerId, buildPagePayload(result.data), { page: 1, url }, now);
  }, [customerId]);

  /**
   * Fetch the next page of `recent_health_records` from the server and append
   * the older records into the cache + UI. Each "Show More" click pulls the
   * next API page; the button stays visible while the server reports more pages
   * via `available_next`. The snapshot and Last_Fetch_Time are left untouched
   * because these are non-page-1 fetches.
   *
   * The starting page is calculated from the total records already loaded
   * (including historical cache). E.g. if 83 records are loaded and pageSize
   * is 20, the next page is floor(83/20) + 1 = 5.
   */
  const fetchNextPage = useCallback(async () => {
    if (isLoadingMore || !hasMoreRecords) return;

    // Calculate the correct next page from how many records are already loaded.
    // This avoids re-fetching pages we already have from historical cache.
    const PAGE_SIZE = 20;
    const loadedCount = records.length;
    const nextPage = Math.floor(loadedCount / PAGE_SIZE) + 1;

    setIsLoadingMore(true);
    try {
      // Subsequent pages (page >= 2) come from HcGetRecentHealthRecords, which
      // returns { records, pagination: { page, pageSize, total_records,
      // available_next } } — a records-only payload with no snapshot.
      const url = `${API_BASE_URL}?func=HcGetRecentHealthRecords&customerId=${customerId}&page=${nextPage}`;
      const response = await fetch(url, {
        headers: {
          'x-api-key': API_KEY
        }
      });

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success || !result.data) {
        throw new Error('API response indicates failure or is missing data.');
      }

      // Records-only page: build a payload with just the fetched records and an
      // empty snapshot (mergePage ignores the snapshot for page !== 1, so the
      // cached profile/notes/vitals are left untouched).
      const pagePayload = {
        records: (result.data?.records ?? []) as RawHealthRecord[],
        snapshot: {
          profile: undefined,
          important_notes: undefined,
          vitals_last_30_days: undefined,
          resident_visitation: undefined,
        } as SnapshotPayload,
      };

      const now = Date.now();
      const entry = mergePage(customerId, pagePayload, { page: nextPage, url }, now);
      applyEntryToState(entry);

      setCurrentPage(nextPage);
      setHasMoreRecords(Boolean(result.data?.pagination?.available_next));
    } catch (err) {
      console.error("Failed to load more records:", err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [customerId, records, hasMoreRecords, isLoadingMore, applyEntryToState]);

  const fetchPatientData = useCallback(async () => {
    setError(null);
    try {
      const entry = await fetchAndMergePage1();
      applyEntryToState(entry);
    } catch (err) {
      console.error("Failed to fetch patient data:", err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred.');
    }
  }, [fetchAndMergePage1, applyEntryToState]);

  // Effect for fetching data on initial component mount — cache-aware (Req 13.1).
  useEffect(() => {
    const initialize = async () => {
        const tier = getTier(customerId, Date.now());

        // Fresh tier (Req 2): serve cached payload, no refetch, no full loader.
        if (tier === 'fresh') {
            const cached = loadCached(customerId);
            if (cached) {
                applyEntryToState(cached);
                // No refetch in fresh tier, so the server's available_next is
                // unknown here. Show the button when a full first page worth of
                // records is cached; the next-page fetch reconciles the real
                // available_next from the server.
                setHasMoreRecords(cached.records.length >= 20);
            }
            setIsLoading(false);
            return;
        }

        // Stale tier (Req 3): show cached payload immediately, then refresh in
        // the background behind the Loading_Bar (reusing refreshProgress).
        if (tier === 'stale') {
            const cached = loadCached(customerId);
            if (cached) applyEntryToState(cached);
            // Cached data is shown without the Full_Loading_Page (Req 3.1, 3.2).
            setIsLoading(false);

            setRefreshProgress(10);
            const progressInterval = setInterval(() => {
                setRefreshProgress(oldProgress => {
                    if (oldProgress === null) {
                        clearInterval(progressInterval);
                        return null;
                    }
                    if (oldProgress >= 90) {
                        return 90;
                    }
                    const diff = Math.random() * 20;
                    return Math.min(oldProgress + diff, 90);
                });
            }, 400);

            try {
                // Background page-1 fetch + merge, then re-render (Req 3.3, 3.4).
                const entry = await fetchAndMergePage1();
                applyEntryToState(entry);
            } catch (err) {
                // Keep displaying the previously cached payload (Req 3.5).
                console.error("Background refresh failed:", err);
            } finally {
                clearInterval(progressInterval);
                setRefreshProgress(100);
                setTimeout(() => {
                    setRefreshProgress(null);
                }, 500);
            }
            return;
        }

        // Expired tier (Req 4) and no-cache fallback (Req 13.4): show the
        // Full_Loading_Page, withhold any cached payload, fetch page 1, then
        // display the merged data and dismiss the loader.
        await fetchPatientData();
        setProgress(100);
        setTimeout(() => {
          setIsLoading(false);
        }, 500);
    };

    initialize();
  }, [customerId, fetchPatientData, fetchAndMergePage1, applyEntryToState]);

  const handleLogin = (role: 'family' | 'staff', id?: string) => {
    if (role === 'family') {
      localStorage.setItem('careGuardianUserRole', 'family');
      localStorage.removeItem('careGuardianEmployeeNumber');
      setUserRole('family');
      setEmployeeNumber(null);
    } else if (role === 'staff' && id) {
      localStorage.setItem('careGuardianUserRole', 'staff');
      localStorage.setItem('careGuardianEmployeeNumber', id);
      setUserRole('staff');
      setEmployeeNumber(id);
    }
  };

  const handleLogout = useCallback(() => {
    localStorage.removeItem('careGuardianUserRole');
    localStorage.removeItem('careGuardianEmployeeNumber');
    setUserRole(null);
    setEmployeeNumber(null);
  }, []);

  // Health Info Panel
  const toggleInputPanel = useCallback(() => {
    setIsInputPanelOpen(prev => !prev);
  }, []);
  
  const closeInputPanel = useCallback(() => {
    setIsInputPanelOpen(false);
  }, []);

  // Appointment Panel
  const toggleAppointmentPanel = useCallback(() => {
    setIsAppointmentPanelOpen(prev => !prev);
  }, []);

  const closeAppointmentPanel = useCallback(() => {
    setIsAppointmentPanelOpen(false);
  }, []);

  const addHealthRecord = useCallback(async (newRecord: HealthRecord) => {
    // Null safety: only add record if it has required fields.
    if (newRecord && newRecord.id && newRecord.date && newRecord.type) {
      // Optimistic update (Req 2.2, 9.1): merge the new record into the cache
      // facade, then reflect the returned Cache_Entry in the UI instantly via
      // the shared transform pipeline so the record appears before the
      // reconciling refresh completes.
      const entry = applyAddRecord(customerId, buildRawFromHealthRecord(newRecord));
      applyEntryToState(entry);
    }
    // Close both panels upon successful record addition.
    setIsInputPanelOpen(false);
    setIsAppointmentPanelOpen(false);
    // Reconcile via a page-1 refresh (Req 13.2): pulls the authoritative
    // server record (and any other server-derived state) into the cache + UI,
    // converging the optimistic state with the server.
    await fetchPatientData();
  }, [customerId, applyEntryToState, fetchPatientData]);

  const handleNewAppointment = useCallback(async (_appointment: { date: Date; time: string; note: string }) => {
    // No dedicated optimistic facade method maps cleanly to a scheduled
    // appointment: the server write (HtInsertVisitation, performed inside
    // AppointmentPanel) creates a *new* visitation row, but the optimistic
    // helpers only mutate existing slices (applyVisitCompleted flips an
    // existing visitation's status; applyAddRecord adds a health record, not a
    // visitation). Inventing an optimistic visitation row would require
    // fabricating server-assigned fields (id, created_at, customer_name) that
    // the snapshot/transform pipeline depends on. So for a new appointment we
    // skip the optimistic step and reconcile purely via a page-1 refresh
    // (Req 13.2), which routes the server's authoritative resident_visitation
    // snapshot back through parseResidentVisitations.
    setIsAppointmentPanelOpen(false);
    await fetchPatientData();
  }, [fetchPatientData]);

  const handleSaveImportantNoteEdit = useCallback(async (id: number, notes: string): Promise<WriteResult> => {
    // Optimistic update (Req 2.2, 9.2): apply the edited note text to the
    // cached snapshot and reflect it in the UI immediately.
    applyEntryToState(applyEditNote(customerId, id, notes));
    const body = buildImportantNoteEditBody(id, parseInt(patient!.id, 10), notes);
    const result = await performWrite(body, 'HcUpdateHealthJournal');
    if (result.success) {
      // Reconcile cache + UI with the server on success (Req 13.2).
      await fetchPatientData();
    }
    // On failure the optimistic update is intentionally NOT rolled back; a
    // later page-1 refresh corrects any divergence. The error is still
    // surfaced to the caller as before.
    return result;
  }, [customerId, applyEntryToState, fetchPatientData, patient]);

  const handleUnflagImportantNote = useCallback(async (id: number): Promise<WriteResult> => {
    // Optimistic update (Req 2.2, 9.3): remove the note from the cached
    // important-notes snapshot and reflect it immediately.
    applyEntryToState(applyUnflagNote(customerId, id));
    const body = buildImportantNoteUnflagBody(id, parseInt(patient!.id, 10));
    const result = await performWrite(body, 'HcUpdateHealthJournal');
    if (result.success) {
      // Reconcile cache + UI with the server on success (Req 13.2).
      await fetchPatientData();
    }
    // Optimistic update is intentionally not rolled back on failure (Req 9).
    return result;
  }, [customerId, applyEntryToState, fetchPatientData, patient]);

  const handleMarkVisitCompleted = useCallback(async (id: number): Promise<WriteResult> => {
    // Optimistic update (Req 2.2, 9.4): flip the visitation's status to
    // Completed in the cached snapshot and reflect it immediately.
    applyEntryToState(applyVisitCompleted(customerId, id));
    const body = buildVisitationStatusUpdate(id, parseInt(patient!.id, 10));
    const result = await performWrite(body, 'HtUpdateVisitation');
    if (result.success) {
      // Reconcile cache + UI with the server on success (Req 13.2).
      await fetchPatientData();
    }
    // Optimistic update is intentionally not rolled back on failure (Req 9).
    return result;
  }, [customerId, applyEntryToState, fetchPatientData, patient]);
  
  const handleRefresh = useCallback(async () => {
    if (refreshProgress !== null) return;

    setRefreshProgress(10);

    const progressInterval = setInterval(() => {
        setRefreshProgress(oldProgress => {
            if (oldProgress === null) {
                clearInterval(progressInterval);
                return null;
            }
            if (oldProgress >= 90) {
                return 90;
            }
            const diff = Math.random() * 20;
            return Math.min(oldProgress + diff, 90);
        });
    }, 400);

    try {
        await fetchPatientData();
    } catch (err) {
        console.error("Refresh failed:", err);
    } finally {
        clearInterval(progressInterval);
        setRefreshProgress(100);
        setTimeout(() => {
            setRefreshProgress(null);
        }, 500);
    }
  }, [fetchPatientData, refreshProgress]);

  if (isLoading) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center h-100 text-primary font-weight-bold bg-light-bg">
        <div className="w-100 text-center" style={{maxWidth: '22rem'}}>
            <img 
                src="https://bin.mycareconcierge.com/email/careconcierge_logo.png" 
                alt="Care Concierge Logo" 
                className="mx-auto mb-5"
                style={{height: '4rem'}}
            />
            <h2 className="font-weight-bold text-primary mb-3">Loading Patient Dashboard</h2>
            <p className="text-muted mb-4" style={{transition: 'opacity 0.3s', height: '1.5rem'}}>{loadingMessage}</p>
            <div className="progress" style={{height: '0.625rem', backgroundColor: 'rgba(162, 173, 214, 0.3)'}}>
                <div 
                    className="progress-bar bg-primary"
                    role="progressbar"
                    style={{ width: `${progress}%`, transition: 'width 1s ease-out' }}>
                </div>
            </div>
        </div>
      </div>
    );
  }
  
  if (!userRole) {
    return <LoginModal onLogin={handleLogin} />;
  }

  if (error || !patient) {
      return (
          <div className="d-flex flex-column align-items-center justify-content-center h-100 bg-light-bg p-4">
              <div className="text-center bg-white p-5 rounded-2xl shadow">
                  <h2 className="h2 font-weight-bold mb-2 text-danger">Failed to Load Care Data</h2>
                  <p className="text-muted">{error || 'Patient data could not be found.'}</p>
              </div>
          </div>
      );
  }


  return (
    <div className="d-flex h-100 bg-light-bg text-body diary-skinner">
      <div className="flex-grow-1 p-md-5 p-4 overflow-auto d-flex flex-column">
        <div className="flex-grow-1">
            <header className="mb-5 d-flex align-items-center justify-content-between">
              <div>
                  <h1 className="font-weight-bold text-primary">Care Guardian</h1>
                  <p className="text-muted">Patient Health Dashboard</p>
              </div>
              <div className="d-flex align-items-center">
                <div className="text-right">
                    <img 
                        src="https://bin.mycareconcierge.com/email/careconcierge_logo.png" 
                        alt="Care Concierge Logo" 
                        style={{height: '3.5rem'}}
                    />
                    {userRole === 'staff' && employeeNumber && (
                        <p className="small text-muted mt-1 font-weight-medium">
                            Employee No: <span className="font-weight-bold text-primary">{employeeNumber}</span>
                        </p>
                    )}
                </div>
              </div>
            </header>
            <main>
            <PatientInfoPanel
                patient={patient}
                records={records}
                vitalHistory={vitalHistory}
                rawVitals={rawVitals}
                importantNotes={importantNotes}
                residentVisitations={residentVisitations}
                onSaveImportantNoteEdit={handleSaveImportantNoteEdit}
                onUnflagImportantNote={handleUnflagImportantNote}
                onMarkVisitCompleted={handleMarkVisitCompleted}
                onEnterInfoClick={toggleInputPanel}
                onBookAppointmentClick={toggleAppointmentPanel}
                onRefreshClick={handleRefresh}
                onLogoutClick={handleLogout}
                refreshProgress={refreshProgress}
                userRole={userRole}
                onShowMore={fetchNextPage}
                hasMoreRecords={hasMoreRecords}
                isLoadingMore={isLoadingMore}
            />
            </main>
        </div>
        <footer className="text-center small text-muted mt-5">
            <p>© Care Concierge 2025. All rights reserved.</p>
        </footer>
      </div>

      {userRole === 'staff' && (
        <div className={`side-panel ${isInputPanelOpen ? 'side-panel-open' : 'side-panel-closed'}`}>
            <HealthInputPanel isOpen={isInputPanelOpen} onClose={closeInputPanel} onAddRecord={addHealthRecord} employeeNumber={employeeNumber!} patientId={patient.id} />
        </div>
      )}

      <div className={`side-panel ${isAppointmentPanelOpen ? 'side-panel-open' : 'side-panel-closed'}`}>
        <AppointmentPanel 
            isOpen={isAppointmentPanelOpen} 
            onClose={closeAppointmentPanel} 
            onAppointmentScheduled={handleNewAppointment} 
            employeeNumber={employeeNumber || '0'} 
            patientId={patient.id}
            userRole={userRole} />
      </div>

      {(isInputPanelOpen || isAppointmentPanelOpen) && (
        <div 
            onClick={() => { closeInputPanel(); closeAppointmentPanel(); }} 
            className={`side-panel-backdrop fade ${isInputPanelOpen || isAppointmentPanelOpen ? 'show' : ''}`}
        ></div>
      )}
    </div>
  );
};

export default App;
